import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
} from '@mui/material';
import {
  Send as SendIcon,
  Campaign as CampaignIcon,
  Message as MessageIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  Analytics as AnalyticsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  ExpandMore as ExpandMoreIcon,
  Phone as PhoneIcon,
  Reply as ReplyIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import api from '../services/api';

interface SMSMessage {
  _id: string;
  messageId: string;
  recipient: {
    phoneNumber: string;
    name?: string;
    guest?: {
      firstName: string;
      lastName: string;
    };
  };
  content: {
    body: string;
    type: string;
  };
  status: string;
  category: string;
  createdAt: string;
  sentTime?: string;
  response?: {
    hasResponse: boolean;
    responseBody?: string;
  };
}

interface Campaign {
  _id: string;
  campaignId: string;
  name: string;
  description: string;
  type: string;
  category: string;
  status: string;
  message: {
    body: string;
  };
  audience: {
    targetType: string;
  };
  metrics: {
    totalRecipients: number;
    messagesSent: number;
    messagesDelivered: number;
    deliveryRate: number;
    totalCost: number;
  };
  createdAt: string;
  scheduling: {
    sendTime?: string;
  };
}

const SMSPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [messages, setMessages] = useState<SMSMessage[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Dialog states
  const [sendDialog, setSendDialog] = useState(false);
  const [campaignDialog, setCampaignDialog] = useState(false);
  const [conversationDialog, setConversationDialog] = useState<{ open: boolean; phoneNumber: string }>({ open: false, phoneNumber: '' });

  // Form states
  const [sendForm, setSendForm] = useState({
    to: '',
    body: '',
    category: 'Custom',
    scheduledTime: null as any
  });

  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    type: 'Immediate',
    category: 'Promotional',
    messageBody: '',
    targetType: 'All Guests',
    sendTime: dayjs().add(1, 'hour'),
    isScheduled: false
  });

  const [conversation, setConversation] = useState<SMSMessage[]>([]);
  const [replyText, setReplyText] = useState('');

  const categories = [
    'Reservation Confirmation',
    'Check-in Reminder',
    'Check-out Reminder', 
    'Payment Reminder',
    'Weather Alert',
    'Event Notification',
    'Amenity Order',
    'Emergency Alert',
    'Marketing',
    'Custom',
    'System Update',
    'Survey Request'
  ];

  const audienceTypes = [
    'All Guests',
    'Current Guests',
    'Upcoming Guests',
    'Past Guests',
    'Custom Filter'
  ];

  useEffect(() => {
    if (activeTab === 0) {
      fetchMessages();
    } else if (activeTab === 1) {
      fetchCampaigns();
    }
  }, [activeTab]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await api.get('/sms/messages', {
        params: { limit: 100 }
      });
      setMessages(response.data.messages);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await api.get('/sms/campaigns');
      setCampaigns(response.data.campaigns);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  const sendSMS = async () => {
    try {
      setLoading(true);
      const payload = {
        to: sendForm.to,
        body: sendForm.body,
        category: sendForm.category,
        ...(sendForm.scheduledTime && { scheduledTime: sendForm.scheduledTime.toISOString() })
      };

      await api.post('/sms/send', payload);
      setSuccessMessage('SMS sent successfully!');
      setSendDialog(false);
      setSendForm({ to: '', body: '', category: 'Custom', scheduledTime: null });
      fetchMessages();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send SMS');
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async () => {
    try {
      setLoading(true);
      const payload = {
        name: campaignForm.name,
        description: campaignForm.description,
        type: campaignForm.isScheduled ? 'Scheduled' : 'Immediate',
        category: campaignForm.category,
        message: {
          body: campaignForm.messageBody,
          personalization: {
            useFirstName: campaignForm.messageBody.includes('{firstName}')
          }
        },
        audience: {
          targetType: campaignForm.targetType,
          excludeOptOuts: true
        },
        ...(campaignForm.isScheduled && {
          scheduling: {
            sendTime: campaignForm.sendTime.toDate()
          }
        }),
        status: campaignForm.isScheduled ? 'Scheduled' : 'Draft'
      };

      await api.post('/sms/campaigns', payload);
      setSuccessMessage('Campaign created successfully!');
      setCampaignDialog(false);
      setCampaignForm({
        name: '',
        description: '',
        type: 'Immediate',
        category: 'Promotional',
        messageBody: '',
        targetType: 'All Guests',
        sendTime: dayjs().add(1, 'hour'),
        isScheduled: false
      });
      fetchCampaigns();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const executeCampaign = async (campaignId: string) => {
    try {
      setLoading(true);
      await api.post(`/sms/campaigns/${campaignId}/execute`);
      setSuccessMessage('Campaign execution started!');
      fetchCampaigns();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to execute campaign');
    } finally {
      setLoading(false);
    }
  };

  const fetchConversation = async (phoneNumber: string) => {
    try {
      const response = await api.get(`/sms/conversation/${phoneNumber}`);
      setConversation(response.data);
      setConversationDialog({ open: true, phoneNumber });
    } catch (err: any) {
      setError('Failed to fetch conversation');
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;

    try {
      await api.post('/sms/send', {
        to: conversationDialog.phoneNumber,
        body: replyText,
        category: 'Custom'
      });
      setReplyText('');
      fetchConversation(conversationDialog.phoneNumber);
      setSuccessMessage('Reply sent successfully!');
    } catch (err: any) {
      setError('Failed to send reply');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Delivered': return 'success';
      case 'Sent': return 'info';
      case 'Failed': return 'error';
      case 'Queued': return 'warning';
      default: return 'default';
    }
  };

  const getCampaignStatusColor = (status: string) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'Sending': return 'info';
      case 'Scheduled': return 'warning';
      case 'Draft': return 'default';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">SMS Management</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => setSendDialog(true)}
          >
            Send SMS
          </Button>
          <Button
            variant="outlined"
            startIcon={<CampaignIcon />}
            onClick={() => setCampaignDialog(true)}
          >
            Create Campaign
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Messages" icon={<MessageIcon />} />
          <Tab label="Campaigns" icon={<CampaignIcon />} />
          <Tab label="Analytics" icon={<AnalyticsIcon />} />
        </Tabs>

        {/* Messages Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            {loading && <CircularProgress />}
            
            <Grid container spacing={2}>
              {messages.map((message) => (
                <Grid item xs={12} key={message._id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                        <Box>
                          <Typography variant="subtitle1">
                            {message.recipient.name || 
                             (message.recipient.guest ? 
                               `${message.recipient.guest.firstName} ${message.recipient.guest.lastName}` : 
                               'Unknown')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {message.recipient.phoneNumber}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip 
                            label={message.status} 
                            color={getStatusColor(message.status) as any}
                            size="small"
                          />
                          <Chip 
                            label={message.category} 
                            variant="outlined"
                            size="small"
                          />
                          {message.response?.hasResponse && (
                            <Badge badgeContent="!" color="primary">
                              <ReplyIcon fontSize="small" />
                            </Badge>
                          )}
                        </Box>
                      </Box>
                      
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {message.content.body}
                      </Typography>
                      
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {dayjs(message.createdAt).format('MMM DD, YYYY h:mm A')}
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<PhoneIcon />}
                          onClick={() => fetchConversation(message.recipient.phoneNumber)}
                        >
                          View Conversation
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Campaigns Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            {loading && <CircularProgress />}
            
            <Grid container spacing={2}>
              {campaigns.map((campaign) => (
                <Grid item xs={12} md={6} key={campaign._id}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                        <Box>
                          <Typography variant="h6">{campaign.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {campaign.description}
                          </Typography>
                        </Box>
                        <Chip
                          label={campaign.status}
                          color={getCampaignStatusColor(campaign.status) as any}
                          size="small"
                        />
                      </Box>

                      <Typography variant="body2" sx={{ mb: 2, p: 1, backgroundColor: 'grey.100', borderRadius: 1 }}>
                        "{campaign.message.body}"
                      </Typography>

                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Recipients</Typography>
                          <Typography variant="body2">{campaign.metrics.totalRecipients}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Delivered</Typography>
                          <Typography variant="body2">
                            {campaign.metrics.messagesDelivered} ({campaign.metrics.deliveryRate.toFixed(1)}%)
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Target</Typography>
                          <Typography variant="body2">{campaign.audience.targetType}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Cost</Typography>
                          <Typography variant="body2">${campaign.metrics.totalCost.toFixed(4)}</Typography>
                        </Grid>
                      </Grid>

                      {campaign.scheduling.sendTime && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          Scheduled: {dayjs(campaign.scheduling.sendTime).format('MMM DD, YYYY h:mm A')}
                        </Typography>
                      )}

                      <Box display="flex" gap={1}>
                        {['Draft', 'Scheduled'].includes(campaign.status) && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<PlayIcon />}
                            onClick={() => executeCampaign(campaign._id)}
                          >
                            Execute
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                        >
                          Edit
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Analytics Tab */}
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>SMS Analytics</Typography>
            <Typography variant="body2" color="text.secondary">
              Detailed analytics coming soon...
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Send SMS Dialog */}
      <Dialog open={sendDialog} onClose={() => setSendDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send SMS Message</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                value={sendForm.to}
                onChange={(e) => setSendForm(prev => ({ ...prev, to: e.target.value }))}
                placeholder="+1234567890"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={sendForm.category}
                  onChange={(e) => setSendForm(prev => ({ ...prev, category: e.target.value }))}
                  label="Category"
                >
                  {categories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <DateTimePicker
                label="Schedule (Optional)"
                value={sendForm.scheduledTime}
                onChange={(value) => setSendForm(prev => ({ ...prev, scheduledTime: value }))}
                minDateTime={dayjs()}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Message"
                value={sendForm.body}
                onChange={(e) => setSendForm(prev => ({ ...prev, body: e.target.value }))}
                placeholder="Type your message here..."
                inputProps={{ maxLength: 1600 }}
              />
              <Typography variant="caption" color="text.secondary">
                {sendForm.body.length}/1600 characters
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialog(false)}>Cancel</Button>
          <Button 
            onClick={sendSMS} 
            variant="contained"
            disabled={!sendForm.to || !sendForm.body || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {sendForm.scheduledTime ? 'Schedule' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Campaign Dialog */}
      <Dialog open={campaignDialog} onClose={() => setCampaignDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create SMS Campaign</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Campaign Name"
                value={campaignForm.name}
                onChange={(e) => setCampaignForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={campaignForm.category}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, category: e.target.value }))}
                  label="Category"
                >
                  {categories.filter(cat => cat !== 'Custom').map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={campaignForm.description}
                onChange={(e) => setCampaignForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Target Audience</InputLabel>
                <Select
                  value={campaignForm.targetType}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, targetType: e.target.value }))}
                  label="Target Audience"
                >
                  {audienceTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={campaignForm.isScheduled}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, isScheduled: e.target.checked }))}
                  />
                }
                label="Schedule for later"
              />
            </Grid>
            {campaignForm.isScheduled && (
              <Grid item xs={12}>
                <DateTimePicker
                  label="Send Time"
                  value={campaignForm.sendTime}
                  onChange={(value) => setCampaignForm(prev => ({ ...prev, sendTime: value || dayjs() }))}
                  minDateTime={dayjs()}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Message Content"
                value={campaignForm.messageBody}
                onChange={(e) => setCampaignForm(prev => ({ ...prev, messageBody: e.target.value }))}
                placeholder="Type your campaign message here... Use {firstName} for personalization."
                inputProps={{ maxLength: 1600 }}
              />
              <Typography variant="caption" color="text.secondary">
                {campaignForm.messageBody.length}/1600 characters
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignDialog(false)}>Cancel</Button>
          <Button 
            onClick={createCampaign} 
            variant="contained"
            disabled={!campaignForm.name || !campaignForm.messageBody || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CampaignIcon />}
          >
            Create Campaign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conversation Dialog */}
      <Dialog 
        open={conversationDialog.open} 
        onClose={() => setConversationDialog({ open: false, phoneNumber: '' })} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Conversation: {conversationDialog.phoneNumber}
        </DialogTitle>
        <DialogContent>
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {conversation.map((msg) => (
              <ListItem key={msg._id} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <Box 
                  sx={{ 
                    alignSelf: msg.recipient.phoneNumber === conversationDialog.phoneNumber ? 'flex-end' : 'flex-start',
                    backgroundColor: msg.recipient.phoneNumber === conversationDialog.phoneNumber ? 'primary.light' : 'grey.200',
                    color: msg.recipient.phoneNumber === conversationDialog.phoneNumber ? 'primary.contrastText' : 'text.primary',
                    p: 1,
                    borderRadius: 2,
                    maxWidth: '80%'
                  }}
                >
                  <Typography variant="body2">{msg.content.body}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {dayjs(msg.createdAt).format('MMM DD, h:mm A')}
                  </Typography>
                </Box>
              </ListItem>
            ))}
          </List>
          
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Type your reply..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendReply()}
            />
            <Button
              variant="contained"
              size="small"
              onClick={sendReply}
              disabled={!replyText.trim()}
            >
              Send
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConversationDialog({ open: false, phoneNumber: '' })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SMSPage;